import {z} from 'zod'

// --- Recipient filter types ---

export const MailerFilterField = z.object({
    key: z.string(),
    operator: z.string(),
    value: z.unknown()
})
export type MailerFilterField = z.infer<typeof MailerFilterField>

export const MailerFilters = z.object({
    operator: z.literal('and'),
    fields: z.array(MailerFilterField)
})
export type MailerFilters = z.infer<typeof MailerFilters>

// --- Form validation schema ---

export const MailerFormValidationSchema = z
    .object({
        subject: z.string().min(1, "L'oggetto è obbligatorio").max(200, "L'oggetto non può superare i 200 caratteri"),
        body: z.string().min(1, 'Il corpo del messaggio è obbligatorio'),
        includePatients: z.boolean(),
        includeConsultants: z.boolean(),
        // Patient filters
        patientMinAge: z.coerce.number().min(0).optional(),
        patientMaxAge: z.coerce.number().min(0).optional(),
        patientGender: z.enum(['male', 'female']).optional(),
        patientResidenceAslDistrictId: z.string().optional(),
        patientOutsideRegion: z.boolean().optional(),
        patientNeuromuscularDiagnosisId: z.string().optional(),
        patientServiceType: z.enum(['adults', 'evolutionary_age', 'evolutionary_age_adults']).optional(),
        // Consultant filters
        consultantSpecializationId: z.string().optional(),
        consultantStatus: z.enum(['operative', 'candidate', 'suspended', 'absent', 'terminated']).optional(),
        consultantContractType: z.enum(['consultant', 'flat_rate', 'employee']).optional(),
        consultantWorkSector: z.enum(['domiciliary', 'outpatient', 'outpatient_domiciliary']).optional()
    })
    .refine(data => data.includePatients || data.includeConsultants, {
        path: ['includePatients'],
        message: 'Seleziona almeno un tipo di destinatario'
    })
export type MailerFormValidationSchema = z.infer<typeof MailerFormValidationSchema>

// --- API request/response types ---

export const SendMailPayload = z.object({
    subject: z.string().min(1),
    body: z.string().min(1),
    recipients: z.object({
        patients: z
            .object({
                filters: MailerFilters.optional()
            })
            .optional(),
        consultants: z
            .object({
                filters: MailerFilters.optional()
            })
            .optional()
    })
})
export type SendMailPayload = z.infer<typeof SendMailPayload>

export const PreviewRecipient = z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string()
})
export type PreviewRecipient = z.infer<typeof PreviewRecipient>

export const SendMailResponse = z.object({
    recipientCount: z.number()
})
export type SendMailResponse = z.infer<typeof SendMailResponse>

// --- History (batches + logs) ---

export const MailLogStatus = z.enum(['pending', 'sent', 'bounced', 'failed'])
export type MailLogStatus = z.infer<typeof MailLogStatus>

export const MailRecipientKind = z.enum(['patient', 'consultant', 'whitelist'])
export type MailRecipientKind = z.infer<typeof MailRecipientKind>

export const MailBatchTriggeredBy = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string()
})
export type MailBatchTriggeredBy = z.infer<typeof MailBatchTriggeredBy>

export const MailBatchSummary = z.object({
    batchId: z.string().uuid(),
    subject: z.string(),
    triggeredBy: MailBatchTriggeredBy.nullable(),
    totalRecipients: z.number(),
    counts: z.object({
        sent: z.number(),
        failed: z.number(),
        bounced: z.number(),
        pending: z.number()
    }),
    createdAt: z.string(),
    lastActivityAt: z.string()
})
export type MailBatchSummary = z.infer<typeof MailBatchSummary>

export const MailLog = z.object({
    id: z.string().uuid(),
    batchId: z.string().uuid(),
    triggeredByUserId: z.string().nullable(),
    subject: z.string(),
    recipient: z.string(),
    recipientKind: MailRecipientKind,
    recipientEntityId: z.string().nullable(),
    messageId: z.string().nullable(),
    status: MailLogStatus,
    attempts: z.number(),
    error: z.string().nullable(),
    sentAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
})
export type MailLog = z.infer<typeof MailLog>
